import {
  CanvasTexture,
  LinearFilter,
  Loader,
  type LoadingManager,
  SRGBColorSpace,
} from 'three'
import type { FillPattern } from '../types/floorplannerTypes'

/**
 * High enough that chair outlines stay sharp at typical editor zoom.
 * No mipmaps — mipmaps turned thin black strokes into grey mush.
 */
export const MAX_SVG_RASTER_EDGE = 3072
/** Bump when raster bake rules change so useLoader does not reuse stale textures. */
export const SVG_RASTER_CACHE_VERSION = 'v4'
export const SVG_RASTER_CACHE_PREFIX = `boni-svg-raster:${SVG_RASTER_CACHE_VERSION}:`

type FillMaterialColor = { color?: unknown }

/**
 * BONI symbol-library drawings (sofas, chairs, botanicals, etc.).
 * These stay vector so 2D stays sharp; catalog/AI footprints are the heavy ones.
 */
export const isSymbolLibrarySvgKey = (libraryKey?: string): boolean => {
  if (!libraryKey) {
    return false
  }
  return (
    libraryKey.startsWith('MANUAL/') || libraryKey.startsWith('FLOOR_PLAN/')
  )
}

/**
 * Raster only catalog / uploaded / untagged footprints. Patterned, dashed,
 * recolored, or symbol-library SVGs stay vector.
 */
export const shouldRasterizeSvg = (options: {
  skipFill?: boolean
  skipStrokes?: boolean
  fillPattern?: FillPattern
  lineType?: string
  fillMaterial?: FillMaterialColor
  /** Symbol library key — MANUAL/ and FLOOR_PLAN/ stay vector. */
  libraryKey?: string
  /** Catalog product 3D footprint (PRODUCT_3D / threeD.product). */
  hasProduct3D?: boolean
  /** Uploaded user-model footprint (USER_MODEL_3D / threeD.userModel). */
  hasUserModel3D?: boolean
}): boolean => {
  if (options.skipFill || options.skipStrokes) {
    return false
  }
  if (options.fillPattern) {
    return false
  }
  if (options.lineType && options.lineType !== 'solid') {
    return false
  }
  const color = options.fillMaterial?.color
  if (typeof color === 'string' && color.trim() !== '') {
    return false
  }
  if (typeof color === 'number') {
    return false
  }

  // Catalog / uploaded footprints are the expensive duplicates (e.g. many stools).
  if (
    options.hasProduct3D ||
    options.hasUserModel3D ||
    options.libraryKey === 'PRODUCT_3D' ||
    options.libraryKey === 'USER_MODEL_3D'
  ) {
    return true
  }

  // Library furniture drawings stay sharp vector (size + stroke fidelity).
  if (isSymbolLibrarySvgKey(options.libraryKey)) {
    return false
  }

  // Untagged / custom SVGs: flatten — often large inline path data.
  return true
}

export const rasterStrokeCss = (color: unknown): string => {
  if (typeof color === 'number' && Number.isFinite(color)) {
    return `#${Math.round(color).toString(16).padStart(6, '0')}`
  }
  if (typeof color === 'string' && color.trim() !== '') {
    if (color.trim().toLowerCase() === 'black') {
      return '#000000'
    }
    return color.trim()
  }
  return '#000000'
}

export const decodeSvgText = (src: string): string | null => {
  const trimmed = src.trim()
  if (trimmed.startsWith('<svg')) {
    return trimmed
  }
  if (!trimmed.startsWith('data:image/svg+xml')) {
    return null
  }

  const comma = trimmed.indexOf(',')
  if (comma < 0) {
    return null
  }

  const payload = trimmed.slice(comma + 1)
  const isBase64 = /;base64/i.test(trimmed.slice(0, comma))
  if (isBase64) {
    try {
      return atob(payload)
    } catch {
      return null
    }
  }

  try {
    return decodeURIComponent(payload)
  } catch {
    return payload
  }
}

export const parseSvgViewBoxSize = (svgText: string): { width: number; height: number } => {
  const viewBoxMatch = svgText.match(/viewBox\s*=\s*["']([^"']+)["']/i)
  if (viewBoxMatch) {
    const parts = viewBoxMatch[1].trim().split(/[\s,]+/).map(Number)
    if (
      parts.length === 4 &&
      Number.isFinite(parts[2]) &&
      Number.isFinite(parts[3]) &&
      parts[2] > 0 &&
      parts[3] > 0
    ) {
      return { width: parts[2], height: parts[3] }
    }
  }

  const widthMatch = svgText.match(/<svg\b[^>]*\bwidth\s*=\s*["']([\d.]+)/i)
  const heightMatch = svgText.match(/<svg\b[^>]*\bheight\s*=\s*["']([\d.]+)/i)
  const width = widthMatch ? Number(widthMatch[1]) : 0
  const height = heightMatch ? Number(heightMatch[1]) : 0
  if (width > 0 && height > 0) {
    return { width, height }
  }

  return { width: MAX_SVG_RASTER_EDGE, height: MAX_SVG_RASTER_EDGE }
}

export const rasterPixelSize = (
  width: number,
  height: number,
  maxEdge = MAX_SVG_RASTER_EDGE,
): { width: number; height: number } => {
  const safeWidth = Math.max(width, 1)
  const safeHeight = Math.max(height, 1)
  const scale = maxEdge / Math.max(safeWidth, safeHeight)
  return {
    width: Math.max(1, Math.round(safeWidth * scale)),
    height: Math.max(1, Math.round(safeHeight * scale)),
  }
}

export const svgWithPixelSize = (svgText: string, width: number, height: number): string =>
  svgText.replace(/<svg\b([^>]*)>/i, (_match, attrs: string) => {
    const next = attrs
      .replace(/\swidth\s*=\s*["'][^"']*["']/gi, '')
      .replace(/\sheight\s*=\s*["'][^"']*["']/gi, '')
    return `<svg${next} width="${width}" height="${height}">`
  })

const STROKE_SHAPE_SELECTOR = 'path, line, polyline, polygon, circle, rect, ellipse'

const readPresentation = (el: Element, name: string): string | null => {
  const attr = el.getAttribute(name)
  if (attr != null && attr.trim() !== '') {
    return attr.trim()
  }
  const style = el.getAttribute('style')
  if (!style) {
    return null
  }
  const match = style.match(new RegExp(`(?:^|;)\\s*${name}\\s*:\\s*([^;]+)`, 'i'))
  return match?.[1]?.trim() ?? null
}

const hasDrawableStroke = (el: Element): boolean => {
  const stroke = readPresentation(el, 'stroke')
  if (stroke == null) {
    // SVG default for paths is often fill black / stroke none; still treat
    // explicit stroke-width as a stroked shape.
    return readPresentation(el, 'stroke-width') != null
  }
  return stroke.toLowerCase() !== 'none'
}

/**
 * Catalog footprint constrain used to emit invalid `<path … //>` (attrs capture
 * included `/`, then another `/` was appended). Browsers refuse those images.
 */
export const repairSvgSelfClosingTags = (svgText: string): string =>
  svgText.replace(/\/{2,}\s*>/g, '/>')

/**
 * Darken stroked shapes to the editor color without rewriting stroke-width.
 * Overriding width made sofas/chairs look chunky vs vector 2D.
 */
export const restyleSvgForRaster = (
  svgText: string,
  options: { stroke: string; lineWeight: number },
): string => {
  const repaired = repairSvgSelfClosingTags(svgText)
  if (typeof DOMParser === 'undefined') {
    const style = `<style><![CDATA[
      path, line, polyline, polygon, circle, rect, ellipse {
        stroke: ${options.stroke} !important;
        stroke-opacity: 1 !important;
      }
    ]]></style>`
    return repaired.replace(/<svg\b[^>]*>/i, (tag) => `${tag}${style}`)
  }

  const doc = new DOMParser().parseFromString(repaired, 'image/svg+xml')
  const root = doc.documentElement
  if (!root || root.tagName.toLowerCase() === 'parsererror') {
    return repaired
  }

  root.querySelectorAll(STROKE_SHAPE_SELECTOR).forEach((el) => {
    if (!hasDrawableStroke(el)) {
      return
    }
    el.setAttribute('stroke', options.stroke)
    el.setAttribute('stroke-opacity', '1')
  })

  // Ensure currentColor fills resolve to the editor stroke (usually black).
  if (!root.getAttribute('color')) {
    root.setAttribute('color', options.stroke)
  }

  return new XMLSerializer().serializeToString(doc)
}

const RASTER_URL_SEPARATOR = '\u001e'

export const toSvgRasterLoaderUrl = (
  src: string,
  style: { stroke: string; lineWeight: number },
): string =>
  `${SVG_RASTER_CACHE_PREFIX}${style.stroke}${RASTER_URL_SEPARATOR}${style.lineWeight}${RASTER_URL_SEPARATOR}${src}`

export const parseSvgRasterLoaderUrl = (
  url: string,
): { src: string; stroke: string; lineWeight: number } => {
  const payload = url.startsWith(SVG_RASTER_CACHE_PREFIX)
    ? url.slice(SVG_RASTER_CACHE_PREFIX.length)
    : url
  const first = payload.indexOf(RASTER_URL_SEPARATOR)
  const second = first < 0 ? -1 : payload.indexOf(RASTER_URL_SEPARATOR, first + 1)
  if (first < 0 || second < 0) {
    return { src: payload, stroke: '#000000', lineWeight: 1 }
  }
  return {
    stroke: payload.slice(0, first),
    lineWeight: Number(payload.slice(first + 1, second)) || 1,
    src: payload.slice(second + 1),
  }
}

const createRasterTexture = (canvas: HTMLCanvasElement): CanvasTexture => {
  const texture = new CanvasTexture(canvas)
  texture.colorSpace = SRGBColorSpace
  texture.generateMipmaps = false
  texture.minFilter = LinearFilter
  texture.magFilter = LinearFilter
  texture.anisotropy = 4
  texture.needsUpdate = true
  return texture
}

/**
 * Vector 2D scales to the path bounding box, not the full viewBox. Cropping
 * transparent padding keeps chairs/tables the same relative size in-plan.
 */
export const cropCanvasToOpaqueContent = (
  source: HTMLCanvasElement,
  paddingPx = 2,
): HTMLCanvasElement => {
  const context = source.getContext('2d', { willReadFrequently: true })
  if (!context) {
    return source
  }

  const { width, height } = source
  if (width < 1 || height < 1) {
    return source
  }

  const { data } = context.getImageData(0, 0, width, height)
  let minX = width
  let minY = height
  let maxX = -1
  let maxY = -1

  for (let y = 0; y < height; y += 1) {
    const row = y * width
    for (let x = 0; x < width; x += 1) {
      if (data[(row + x) * 4 + 3] > 8) {
        if (x < minX) minX = x
        if (y < minY) minY = y
        if (x > maxX) maxX = x
        if (y > maxY) maxY = y
      }
    }
  }

  if (maxX < minX || maxY < minY) {
    return source
  }

  const pad = Math.max(0, Math.floor(paddingPx))
  minX = Math.max(0, minX - pad)
  minY = Math.max(0, minY - pad)
  maxX = Math.min(width - 1, maxX + pad)
  maxY = Math.min(height - 1, maxY + pad)

  const cropWidth = maxX - minX + 1
  const cropHeight = maxY - minY + 1
  if (cropWidth >= width && cropHeight >= height) {
    return source
  }

  const cropped = document.createElement('canvas')
  cropped.width = cropWidth
  cropped.height = cropHeight
  const croppedContext = cropped.getContext('2d')
  // In browsers we copy pixels; in tests the canvas mock may lack a 2D context.
  croppedContext?.drawImage(
    source,
    minX,
    minY,
    cropWidth,
    cropHeight,
    0,
    0,
    cropWidth,
    cropHeight,
  )
  return cropped
}

export const rasterizeSvgSource = (
  src: string,
  style: { stroke: string; lineWeight: number } = { stroke: '#000000', lineWeight: 1 },
): Promise<CanvasTexture> =>
  new Promise((resolve, reject) => {
    const svgText = decodeSvgText(src)
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = Math.max(1, image.naturalWidth || image.width || 1)
        canvas.height = Math.max(1, image.naturalHeight || image.height || 1)
        const context = canvas.getContext('2d')
        if (!context) {
          reject(new Error('Could not rasterize SVG'))
          return
        }
        context.imageSmoothingEnabled = true
        context.imageSmoothingQuality = 'high'
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        const cropped = cropCanvasToOpaqueContent(canvas)
        resolve(createRasterTexture(cropped))
      } catch (error) {
        reject(error)
      }
    }
    image.onerror = () => {
      reject(new Error('Failed to load SVG image'))
    }

    if (svgText) {
      const restyled = restyleSvgForRaster(svgText, style)
      const intrinsic = parseSvgViewBoxSize(restyled)
      const pixels = rasterPixelSize(intrinsic.width, intrinsic.height)
      const sized = svgWithPixelSize(restyled, pixels.width, pixels.height)
      image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(sized)}`
      return
    }

    image.src = src
  })

/**
 * R3F `useLoader` cache key is this URL, so identical products share one texture.
 */
export class SvgRasterLoader extends Loader {
  constructor(manager?: LoadingManager) {
    super(manager)
  }

  load(
    url: string,
    onLoad: (texture: CanvasTexture) => void,
    _onProgress?: (event: ProgressEvent) => void,
    onError?: (error: unknown) => void,
  ): void {
    const { src, stroke, lineWeight } = parseSvgRasterLoaderUrl(url)
    rasterizeSvgSource(src, { stroke, lineWeight }).then(onLoad).catch((error) => {
      onError?.(error)
    })
  }
}
